import { Component, Input, OnInit, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MessageService } from 'primeng/api';
import Swal from 'sweetalert2';
import { IAccion, ITipoOpcion, IResultado, IResultadoAccion, IResultadoCob, ITarea } from '../interfaces/accion.interface';
import { AccionService } from '../services/accion.service';
import { SessionService } from '@shared/helpers/sesion.service';

@Component({
  selector: 'app-abc-acciones-form',
  templateUrl: './abc-acciones-form.component.html',
  providers: [MessageService]
})
export class AbcAccionesFormComponent implements OnInit, OnChanges {
  @Input() accion: IAccion | null = null;
  @Input() isEditing: boolean = false;
  @Output() onSave = new EventEmitter<{ success: boolean; message: string }>();
  @Output() onClose = new EventEmitter<void>();
  
  form!: FormGroup;
  loading: boolean = false;
  tiposOpciones: ITipoOpcion[] = [];
  resultados: IResultadoAccion[] = [];
  
  // Propiedades para el diálogo de resultados
  displayResultadosDialog: boolean = false;
  resultadosCob: IResultadoCob[] = [];
  loadingResultados: boolean = false;
  first: number = 0;
  rows: number = 10;
  totalRecords: number = 0;
  selectedResultado: IResultadoCob | null = null;
  
  // Propiedades para el diálogo de detalles del resultado
  displayDetalleResultadoDialog: boolean = false;
  resultadoSeleccionado: IResultadoAccion | null = null;
  tareas: ITarea[] = []; // Array para las tareas
  loadingTareas: boolean = false;
  tareasCount: { [key: string]: number } = {}; // Conteo de tareas por resultado

  // Propiedades para el diálogo de tareas
  displayTareasDialog: boolean = false;
  tareaForm!: FormGroup;
  tareasOpciones: any[] = [];
  codigosOpciones: any[] = [];
  loadingTarea: boolean = false;
  editandoTarea: boolean = false;
  tareaEditando: ITarea | null = null;

  constructor(
    private fb: FormBuilder,
    private messageService: MessageService,
    private accionService: AccionService,
    private sessionService: SessionService
  ) {
    this.initTareaForm();
  }

  ngOnInit(): void {
    this.initForm();
    this.loadTiposOpciones();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['accion']) {
      this.initForm(); // Siempre reinicializar el formulario cuando cambie el modelo
      if (changes['accion'].currentValue && this.tiposOpciones.length > 0) {
        this.loadAccion();
      }
    }
  }

  initForm(): void {
    this.form = this.fb.group({
      accion: [{ value: '', disabled: !!this.accion }, [Validators.required]],
      descripcion: ['', [Validators.required]],
      costo: ['', [Validators.required, Validators.min(0)]],
      tipo: ['', [Validators.required]]
    });
    this.resultados = []; // Inicializar resultados vacíos
  }

  loadTiposOpciones(): void {
    this.accionService.getTiposOpciones().subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.tiposOpciones = response.data;
          console.log('Opciones cargadas:', this.tiposOpciones);
          // Después de cargar las opciones, inicializar el formulario si hay datos de edición
          if (this.accion) {
            console.log('Acción a editar:', this.accion);
            this.loadAccion();
          }
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'No se pudieron cargar las opciones de tipos'
          });
        }
      },
      error: (error) => {
        console.error('Error:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Error al cargar las opciones de tipos'
        });
      }
    });
  }

  loadAccion(): void {
    if (!this.accion?.saccion) return;
    
    this.loading = true;
    console.log('Cargando acción con tipo:', this.accion.tipo);
    console.log('Opciones disponibles:', this.tiposOpciones);
    
    // Buscar si existe una opción que coincida con el tipo de la acción
    // Primero buscar por sOpcion (nombre), luego por sValor (código)
    const opcionEncontrada = this.tiposOpciones.find(opcion => 
      (opcion.sOpcion && this.accion.tipo && opcion.sOpcion.toLowerCase() === this.accion.tipo.toLowerCase()) || 
      opcion.sValor === this.accion.tipo
    );
    
    console.log('Opción encontrada:', opcionEncontrada);
    
    // Si no se encuentra la opción, mostrar un mensaje de advertencia
    if (!opcionEncontrada && this.accion.tipo) {
      console.warn(`No se encontró la opción "${this.accion.tipo}" en las opciones disponibles`);
      this.messageService.add({
        severity: 'warn',
        summary: 'Advertencia',
        detail: `El tipo "${this.accion.tipo}" no está disponible en las opciones actuales`
      });
    }
    
    // TODO: Implementar carga de acción específica cuando se tenga el servicio
    // Por ahora, usamos los datos que ya tenemos
    this.form.patchValue({
      accion: this.accion.saccion,
      descripcion: this.accion.descripcion,
      costo: this.accion.costo,
      tipo: opcionEncontrada ? opcionEncontrada.sValor : ''
    });
    
    console.log('Valor asignado al formulario:', this.form.get('tipo')?.value);
    
    // Forzar la detección de cambios para el dropdown
    setTimeout(() => {
      this.form.get('tipo')?.updateValueAndValidity();
      this.loading = false;
    }, 100);

    // Cargar resultados de la acción
    this.loadResultadosAccion();
  }

  loadResultadosAccion(): void {
    if (!this.accion?.saccion) return;

    this.accionService.getResultadosAccion(this.accion.saccion).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.resultados = response.data;
          // Cargar el conteo de tareas para cada resultado
          this.cargarConteoTareas();
        } else {
          this.resultados = [];
        }
      },
      error: (error) => {
        console.error('Error cargando resultados:', error);
        this.resultados = [];
      }
    });

    // También ejecutar el count (como solicitaste, solo ejecutarlo)
    this.accionService.getResultadosAccionCount(this.accion.saccion).subscribe({
      next: (response) => {
        console.log('Count de resultados:', response);
      },
      error: (error) => {
        console.error('Error en count de resultados:', error);
      }
    });
  }

  cargarConteoTareas(): void {
    if (!this.accion?.saccion) return;

    this.resultados.forEach(resultado => {
      this.accionService.getTareasCount(this.accion!.saccion, resultado.sResultado).subscribe({
        next: (response) => {
          if (response.success && response.data && response.data.length > 0) {
            this.tareasCount[resultado.sResultado] = response.data[0].cantidad;
          } else {
            this.tareasCount[resultado.sResultado] = 0;
          }
        },
        error: (error) => {
          console.error('Error cargando conteo de tareas para:', resultado.sResultado, error);
          this.tareasCount[resultado.sResultado] = 0;
        }
      });
    });
  }

  openResultados(): void {
    this.displayResultadosDialog = true;
    this.loadResultadosCob();
  }

  loadResultadosCob(): void {
    this.loadingResultados = true;
    
    this.accionService.getResultadosCob().subscribe({
      next: (response) => {
        console.log('Respuesta del servicio:', response);
        if (response.success) {
          this.resultadosCob = response.data;
          this.totalRecords = response.data.length;
          console.log('Resultados cargados:', this.resultadosCob);
          console.log('Total de registros:', this.totalRecords);
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'No se pudieron cargar los resultados'
          });
        }
      },
      error: (error) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Error al cargar los resultados'
        });
        console.error('Error:', error);
      },
      complete: () => {
        this.loadingResultados = false;
      }
    });
  }

  onResultadosPage(event: any): void {
    this.first = event.first;
    this.rows = event.rows;
    this.loadResultadosCob();
  }

  closeResultadosDialog(): void {
    this.displayResultadosDialog = false;
    this.selectedResultado = null;
  }

  onResultadoSelected(resultado: IResultadoCob): void {
    if (!this.accion?.saccion) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'No se pudo identificar la acción'
      });
      return;
    }

    this.loadingResultados = true;
    
    // Convertir "SÍ"/"NO" a 1/0
    const efectivo = resultado.efectivo === 1 ? 1 : 0;
    
    this.accionService.addResultadoAccion(
      this.accion.saccion,
      resultado.sResultado,
      resultado.descripcion,
      efectivo
    ).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.messageService.add({
            severity: 'success',
            summary: 'Éxito',
            detail: `Resultado "${resultado.sResultado}" agregado correctamente`
          });
          // Recargar la tabla de resultados del formulario padre
          this.loadResultadosAccion();
          // Limpiar la selección
          this.selectedResultado = null;
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Error al agregar el resultado'
          });
        }
      },
      error: (error) => {
        console.error('Error:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Error al agregar el resultado'
        });
      },
      complete: () => {
        this.loadingResultados = false;
      }
    });
  }

  create(): void {
    if (this.form.invalid) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Por favor complete todos los campos requeridos'
      });
      return;
    }

    this.loading = true;
    const formData = this.form.getRawValue();
    
    this.accionService.createAccion(
      formData.accion,
      formData.descripcion,
      formData.tipo,
      formData.costo
    ).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.onSave.emit({
            success: true,
            message: 'Acción creada correctamente'
          });
          this.closeForm();
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Error al crear la acción'
          });
        }
        this.loading = false;
      },
      error: (error) => {
        console.error('Error:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Error al crear la acción'
        });
        this.loading = false;
      }
    });
  }

  update(): void {
    if (this.form.invalid) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Por favor complete todos los campos requeridos'
      });
      return;
    }

    this.loading = true;
    const formData = this.form.getRawValue();
    
    this.accionService.updateAccion(
      formData.accion,
      formData.descripcion,
      formData.tipo,
      formData.costo
    ).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.onSave.emit({
            success: true,
            message: 'Acción actualizada correctamente'
          });
          this.closeForm();
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Error al actualizar la acción'
          });
        }
        this.loading = false;
      },
      error: (error) => {
        console.error('Error:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Error al actualizar la acción'
        });
        this.loading = false;
      }
    });
  }

  verResultado(resultado: IResultadoAccion): void {
    this.resultadoSeleccionado = resultado;
    this.displayDetalleResultadoDialog = true;
    this.loadTareas(resultado.sResultado);
  }

  closeDetalleResultadoDialog(): void {
    this.displayDetalleResultadoDialog = false;
    this.resultadoSeleccionado = null;
    this.tareas = [];
  }

  loadTareas(resultado: string): void {
    if (!this.accion?.saccion) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'No se pudo identificar la acción'
      });
      return;
    }

    this.loadingTareas = true;
    
    // Cargar tareas
    this.accionService.getTareas(this.accion.saccion, resultado).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.tareas = response.data;
          console.log('Tareas cargadas:', this.tareas);
        } else {
          this.tareas = [];
          this.messageService.add({
            severity: 'warn',
            summary: 'Advertencia',
            detail: 'No se encontraron tareas para este resultado'
          });
        }
      },
      error: (error) => {
        console.error('Error cargando tareas:', error);
        this.tareas = [];
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Error al cargar las tareas'
        });
      },
      complete: () => {
        this.loadingTareas = false;
      }
    });

    // También ejecutar el count (como solicitaste, solo ejecutarlo)
    this.accionService.getTareasCount(this.accion.saccion, resultado).subscribe({
      next: (response) => {
        console.log('Count de tareas:', response);
      },
      error: (error) => {
        console.error('Error en count de tareas:', error);
      }
    });
  }

  abrirTareas(): void {
    this.editandoTarea = false;
    this.tareaEditando = null;
    this.displayTareasDialog = true;
    this.initTareaForm();
    this.loadTareasOpciones();
  }

  closeTareasDialog(): void {
    this.displayTareasDialog = false;
    this.editandoTarea = false;
    this.tareaEditando = null;
    if (this.tareaForm) {
      this.tareaForm.reset();
    }
  }

  closeForm(): void {
    this.onClose.emit();
  }

  cargarDatosTareaEnFormulario(tarea: ITarea): void {
    // Buscar las opciones correspondientes para mapear correctamente
    const tareaEncontrada = this.tareasOpciones.find(opcion => 
      opcion.sOpcion === tarea.tipo || opcion.sValor === tarea.tipo
    );
    
    const codigoEncontrado = this.codigosOpciones.find(opcion => 
      opcion.sOpcion === tarea.subTipo || opcion.sValor === tarea.subTipo
    );
    
    this.tareaForm.patchValue({
      tarea: tareaEncontrada ? tareaEncontrada.sValor : tarea.tipo,
      codigo: codigoEncontrado ? codigoEncontrado.sValor : tarea.subTipo,
      costo: tarea.costo
    });
  }

  initTareaForm(): void {
    this.tareaForm = this.fb.group({
      tarea: ['', [Validators.required]],
      codigo: ['', [Validators.required]],
      costo: ['', [Validators.required, Validators.min(0)]]
    });

    // Listener para cuando cambie la tarea, actualizar los códigos
    this.tareaForm.get('tarea')?.valueChanges.subscribe(tarea => {
      if (tarea) {
        // Limpiar el campo código cuando cambie la tarea
        this.tareaForm.get('codigo')?.setValue('');
        this.codigosOpciones = [];
        // Cargar los códigos correspondientes a la tarea seleccionada
        this.loadCodigosOpciones();
      }
    });
  }

  loadTareasOpciones(): void {
    this.accionService.getTareasOpciones().subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.tareasOpciones = response.data;
          console.log('Opciones de tareas cargadas:', this.tareasOpciones);
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'No se pudieron cargar las opciones de tareas'
          });
        }
      },
      error: (error) => {
        console.error('Error:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Error al cargar las opciones de tareas'
        });
      }
    });
  }

  loadCodigosOpciones(): void {
    const tareaSeleccionada = this.tareaForm.get('tarea')?.value;
    
    if (!tareaSeleccionada) {
      this.codigosOpciones = [];
      return;
    }

    // Obtener el usuario actual
    const sUsuario = this.sessionService.getUserSession().replace(/"/g, '');

    // Determinar qué servicio usar basado en la tarea seleccionada
    if (tareaSeleccionada === 'Carta') {
      this.accionService.getCodigosCarta(sUsuario).subscribe({
        next: (response) => {
          if (response.success && response.data) {
            // Formatear los datos para mostrar sClave | sDescripción
            this.codigosOpciones = response.data.map((item: any) => ({
              sOpcion: `${item.sClave} | ${item.sDescripcion}`,
              sValor: item.sClave
            }));
            console.log('Opciones de códigos Carta cargadas:', this.codigosOpciones);
          } else {
            this.codigosOpciones = [];
            this.messageService.add({
              severity: 'warn',
              summary: 'Advertencia',
              detail: 'No se encontraron códigos para la tarea Carta'
            });
          }
        },
        error: (error) => {
          console.error('Error:', error);
          this.codigosOpciones = [];
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Error al cargar los códigos de Carta'
          });
        }
      });
    } else if (tareaSeleccionada === 'Clasificador') {
      this.accionService.getCodigosClasificador().subscribe({
        next: (response) => {
          if (response.success && response.data) {
            // Formatear los datos para mostrar clasificador | descripcion
            this.codigosOpciones = response.data.map((item: any) => ({
              sOpcion: `${item.clasificador} | ${item.descripcion}`,
              sValor: item.clasificador
            }));
            console.log('Opciones de códigos Clasificador cargadas:', this.codigosOpciones);
          } else {
            this.codigosOpciones = [];
            this.messageService.add({
              severity: 'warn',
              summary: 'Advertencia',
              detail: 'No se encontraron códigos para la tarea Clasificador'
            });
          }
        },
        error: (error) => {
          console.error('Error:', error);
          this.codigosOpciones = [];
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Error al cargar los códigos de Clasificador'
          });
        }
      });
    } else if (tareaSeleccionada === 'Cobro Especial') {
      this.accionService.getCodigosCobroEspecial().subscribe({
        next: (response) => {
          if (response.success && response.data) {
            // Formatear los datos para mostrar tipoCobro | descripcion
            this.codigosOpciones = response.data.map((item: any) => ({
              sOpcion: `${item.tipoCobro} | ${item.descripcion}`,
              sValor: item.tipoCobro
            }));
            console.log('Opciones de códigos Cobro especial cargadas:', this.codigosOpciones);
          } else {
            this.codigosOpciones = [];
            this.messageService.add({
              severity: 'warn',
              summary: 'Advertencia',
              detail: 'No se encontraron códigos para la tarea Cobro especial'
            });
          }
        },
        error: (error) => {
          console.error('Error:', error);
          this.codigosOpciones = [];
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Error al cargar los códigos de Cobro especial'
          });
        }
      });
    } else if (tareaSeleccionada === 'Contacto') {
      this.accionService.getCodigosContacto().subscribe({
        next: (response) => {
          if (response.success && response.data) {
            // Formatear los datos para mostrar saccion | descripcion
            this.codigosOpciones = response.data.map((item: any) => ({
              sOpcion: `${item.saccion} | ${item.descripcion}`,
              sValor: item.saccion
            }));
            console.log('Opciones de códigos Contacto cargadas:', this.codigosOpciones);
          } else {
            this.codigosOpciones = [];
            this.messageService.add({
              severity: 'warn',
              summary: 'Advertencia',
              detail: 'No se encontraron códigos para la tarea Contacto'
            });
          }
        },
        error: (error) => {
          console.error('Error:', error);
          this.codigosOpciones = [];
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Error al cargar los códigos de Contacto'
          });
        }
      });
    } else if (tareaSeleccionada === 'Estatus') {
      this.accionService.getCodigosEstatus().subscribe({
        next: (response) => {
          if (response.success && response.data) {
            // Formatear los datos para mostrar estatus | descripcion
            this.codigosOpciones = response.data.map((item: any) => ({
              sOpcion: `${item.estatus} | ${item.descripcion}`,
              sValor: item.estatus
            }));
            console.log('Opciones de códigos Estatus cargadas:', this.codigosOpciones);
          } else {
            this.codigosOpciones = [];
            this.messageService.add({
              severity: 'warn',
              summary: 'Advertencia',
              detail: 'No se encontraron códigos para la tarea Estatus'
            });
          }
        },
        error: (error) => {
          console.error('Error:', error);
          this.codigosOpciones = [];
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Error al cargar los códigos de Estatus'
          });
        }
      });
    } else if (tareaSeleccionada === 'Investigacion') {
      this.accionService.getCodigosInvestigacion().subscribe({
        next: (response) => {
          if (response.success && response.data) {
            // Formatear los datos para mostrar sClaveCues | descripcion
            this.codigosOpciones = response.data.map((item: any) => ({
              sOpcion: `${item.sClaveCues} | ${item.sDescripcion}`,
              sValor: item.sClaveCues
            }));
            console.log('Opciones de códigos Investigacion cargadas:', this.codigosOpciones);
          } else {
            this.codigosOpciones = [];
            this.messageService.add({
              severity: 'warn',
              summary: 'Advertencia',
              detail: 'No se encontraron códigos para la tarea Investigacion'
            });
          }
        },
        error: (error) => {
          console.error('Error:', error);
          this.codigosOpciones = [];
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Error al cargar los códigos de Investigacion'
          });
        }
      });
    } else if (tareaSeleccionada === 'Promesa') {
      this.accionService.getCodigosClasificador().subscribe({
        next: (response) => {
          if (response.success && response.data) {
            // Formatear los datos para mostrar clasificador | descripcion
            this.codigosOpciones = response.data.map((item: any) => ({
              sOpcion: `${item.clasificador} | ${item.descripcion}`,
              sValor: item.clasificador
            }));
            console.log('Opciones de códigos Promesa cargadas:', this.codigosOpciones);
          } else {
            this.codigosOpciones = [];
            this.messageService.add({
              severity: 'warn',
              summary: 'Advertencia',
              detail: 'No se encontraron códigos para la tarea Promesa'
            });
          }
        },
        error: (error) => {
          console.error('Error:', error);
          this.codigosOpciones = [];
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Error al cargar los códigos de Promesa'
          });
        }
      });
    } else if (tareaSeleccionada === 'Ruta Critica') {
      this.accionService.getCodigosRutaCritica().subscribe({
        next: (response) => {
          if (response.success && response.data) {
            // Formatear los datos para mostrar numero_Proceso | descripcion
            this.codigosOpciones = response.data.map((item: any) => ({
              sOpcion: `${item.numero_Proceso} | ${item.descriptivo}`,
              sValor: item.numero_Proceso
            }));
            console.log('Opciones de códigos Ruta Critica cargadas:', this.codigosOpciones);
          } else {
            this.codigosOpciones = [];
            this.messageService.add({
              severity: 'warn',
              summary: 'Advertencia',
              detail: 'No se encontraron códigos para la tarea Ruta Critica'
            });
          }
        },
        error: (error) => {
          console.error('Error:', error);
          this.codigosOpciones = [];
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Error al cargar los códigos de Ruta Critica'
          });
        }
      });
    } else if (tareaSeleccionada === 'Segmentador') {
      this.accionService.getCodigosSegmentador().subscribe({
        next: (response) => {
          if (response.success && response.data) {
            // Formatear los datos para mostrar segmentador | descripcion
            this.codigosOpciones = response.data.map((item: any) => ({
              sOpcion: `${item.segmentador} | ${item.descripcion}`,
              sValor: item.segmentador
            }));
            console.log('Opciones de códigos Segmentador cargadas:', this.codigosOpciones);
          } else {
            this.codigosOpciones = [];
            this.messageService.add({
              severity: 'warn',
              summary: 'Advertencia',
              detail: 'No se encontraron códigos para la tarea Segmentador'
            });
          }
        },
        error: (error) => {
          console.error('Error:', error);
          this.codigosOpciones = [];
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Error al cargar los códigos de Segmentador'
          });
        }
      });
    } else if (tareaSeleccionada === 'Supervisor') {
      this.accionService.getCodigosSupervisor().subscribe({
        next: (response) => {
          if (response.success && response.data) {
            // Formatear los datos para mostrar sAgencia | sNombre
            this.codigosOpciones = response.data.map((item: any) => ({
              sOpcion: `${item.sAgencia} | ${item.sNombre}`,
              sValor: item.sAgencia
            }));
            console.log('Opciones de códigos Supervisor cargadas:', this.codigosOpciones);
          } else {
            this.codigosOpciones = [];
            this.messageService.add({
              severity: 'warn',
              summary: 'Advertencia',
              detail: 'No se encontraron códigos para la tarea Supervisor'
            });
          }
        },
        error: (error) => {
          console.error('Error:', error);
          this.codigosOpciones = [];
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Error al cargar los códigos de Supervisor'
          });
        }
      });
    } else if (tareaSeleccionada === 'Visita') {
      this.accionService.getCodigosInvestigacion().subscribe({
        next: (response) => {
          if (response.success && response.data) {
            // Formatear los datos para mostrar sClaveCues | sDescripcion
            this.codigosOpciones = response.data.map((item: any) => ({
              sOpcion: `${item.sClaveCues} | ${item.sDescripcion}`,
              sValor: item.sClaveCues
            }));
            console.log('Opciones de códigos Visita cargadas:', this.codigosOpciones);
          } else {
            this.codigosOpciones = [];
            this.messageService.add({
              severity: 'warn',
              summary: 'Advertencia',
              detail: 'No se encontraron códigos para la tarea Visita'
            });
          }
        },
        error: (error) => {
          console.error('Error:', error);
          this.codigosOpciones = [];
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Error al cargar los códigos de Visita'
          });
        }
      });
    } else {
      // Para otras tareas, usar el servicio genérico (puedes agregar más casos aquí)
      this.accionService.getCodigosOpciones().subscribe({
        next: (response) => {
          if (response.success && response.data) {
            this.codigosOpciones = response.data;
            console.log('Opciones de códigos genéricas cargadas:', this.codigosOpciones);
          } else {
            this.codigosOpciones = [];
            this.messageService.add({
              severity: 'warn',
              summary: 'Advertencia',
              detail: 'No se encontraron códigos para esta tarea'
            });
          }
        },
        error: (error) => {
          console.error('Error:', error);
          this.codigosOpciones = [];
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Error al cargar las opciones de códigos'
          });
        }
      });
    }
  }

  saveTarea(): void {
    if (this.tareaForm.invalid) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Por favor complete todos los campos requeridos'
      });
      return;
    }

    if (!this.accion?.saccion || !this.resultadoSeleccionado?.sResultado) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'No se pudo identificar la acción o resultado'
      });
      return;
    }

    this.loadingTarea = true;
    const formData = this.tareaForm.getRawValue();
    
    // Usar el método correcto según si estamos editando o creando
    const serviceCall = this.editandoTarea 
      ? this.accionService.updateTarea(
          this.accion.saccion,
          this.resultadoSeleccionado.sResultado,
          formData.tarea,
          formData.codigo,
          formData.costo
        )
      : this.accionService.addTarea(
          this.accion.saccion,
          this.resultadoSeleccionado.sResultado,
          formData.tarea,
          formData.codigo,
          formData.costo
        );
    
    serviceCall.subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.messageService.add({
            severity: 'success',
            summary: 'Éxito',
            detail: this.editandoTarea ? 'Tarea actualizada correctamente' : 'Tarea guardada correctamente'
          });
          // Recargar la tabla de tareas
          this.loadTareas(this.resultadoSeleccionado!.sResultado);
          this.closeTareasDialog();
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: this.editandoTarea ? 'Error al actualizar la tarea' : 'Error al guardar la tarea'
          });
        }
        this.loadingTarea = false;
      },
      error: (error) => {
        console.error('Error:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Error al guardar la tarea'
        });
        this.loadingTarea = false;
      }
    });
  }

  editarTarea(tarea: ITarea): void {
    this.tareaEditando = tarea;
    this.editandoTarea = true;
    this.displayTareasDialog = true;
    this.initTareaForm();
    this.loadTareasOpciones();
    
    // Cargar los datos en el formulario después de que se carguen las opciones
    setTimeout(() => {
      this.cargarDatosTareaEnFormulario(tarea);
    }, 100);
  }

  borrarTarea(tarea: ITarea): void {
    Swal.fire({
      title: '¿Está seguro?',
      text: `¿Desea eliminar la tarea: ${tarea.tipo} - ${tarea.subTipo}?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      iconColor: '#d33',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar'
    }).then((result) => {
      if (result.isConfirmed) {
        this.confirmarBorradoTarea(tarea);
      }
    });
  }

  borrarResultado(resultado: IResultadoAccion): void {
    Swal.fire({
      title: '¿Está seguro?',
      text: `¿Desea eliminar el resultado: ${resultado.sResultado} - ${resultado.descripcion}?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      iconColor: '#d33',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar'
    }).then((result) => {
      if (result.isConfirmed) {
        this.confirmarBorradoResultado(resultado);
      }
    });
  }

  confirmarBorradoTarea(tarea: ITarea): void {
    if (!this.accion?.saccion || !this.resultadoSeleccionado?.sResultado) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'No se pudo identificar la acción o resultado'
      });
      return;
    }

    this.accionService.deleteTarea(
      this.accion.saccion,
      this.resultadoSeleccionado.sResultado,
      tarea.tipo,
      tarea.subTipo
    ).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.messageService.add({
            severity: 'success',
            summary: 'Éxito',
            detail: 'Tarea eliminada correctamente'
          });
          
          // Recargar la tabla de tareas después de eliminar
          this.loadTareas(this.resultadoSeleccionado!.sResultado);
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'No se pudo eliminar la tarea'
          });
        }
      },
      error: (error) => {
        console.error('Error:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Error al eliminar la tarea'
        });
      }
    });
  }

  confirmarBorradoResultado(resultado: IResultadoAccion): void {
    if (!this.accion?.saccion) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'No se pudo identificar la acción'
      });
      return;
    }

    this.accionService.deleteResultado(
      this.accion.saccion,
      resultado.sResultado
    ).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.messageService.add({
            severity: 'success',
            summary: 'Éxito',
            detail: 'Resultado eliminado correctamente'
          });
          
          // Recargar la tabla de resultados después de eliminar
          this.loadResultadosAccion();
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'No se pudo eliminar el resultado'
          });
        }
      },
      error: (error) => {
        console.error('Error:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Error al eliminar el resultado'
        });
      }
    });
  }
} 